import { useRouter } from 'next/router';
import { type CSSProperties, memo, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTrackClickedPublicBoardLink } from '@air/analytics';
import { useAirModal } from '@air/provider-modal';
import { cn } from '@air/utils-tailwind';

import { usePublicPageViewNotification } from '~/components/AirNotifications/hooks/usePublicPageViewNotification';
import { usePublicBoardPage } from '~/components/Layouts/PublicBoardLayout/hooks/usePublicBoardPage';
import { PublicBoardAnonymousSignUpModal } from '~/components/PublicLinkAnonymousSignUpModal/PublicBoardAnonymousSignUpModal';
import { usePublicShareWorkspaceAccess } from '~/components/PublicShareWorkspaceAccessProvider';
import { PublicSearch } from '~/components/Search/PublicSearch';
import {
  BOARD_PAGE_HEADER_COLUMN_CLASS_NAME,
  getBoardPageMaxWidthStyle,
} from '~/components/v2/components/board/boardPageColumn';
import { PublicBoard } from '~/components/v2/components/board/PublicBoard';
import { PublicBoardBottomBar } from '~/components/v2/components/board/PublicBoardBottomBar';
import { PublicBoardPageGalleryView } from '~/components/v2/components/board/PublicBoardPageGalleryView';
import { PublicBoardPageSeoTitle } from '~/components/v2/components/board/PublicBoardPageSeoTitle';
import { PublicBoardPageTableView } from '~/components/v2/components/board/PublicBoardPageTableView';
import { PublicBoardTopbarBreadcrumbs } from '~/components/v2/components/board/PublicBoardTopbarBreadcrumbs';
import { PublicBoardTopbarDownloadMenu } from '~/components/v2/components/board/PublicBoardTopbarDownloadMenu';
import {
  ScrollElementContextConsumer,
  ScrollElementContextProvider,
} from '~/components/v2/components/shared/scroll/ScrollElementContextProvider';
import { PublicBoardsAndAssetsSelectionBar } from '~/components/v2/components/shared/selectionBar/PublicBoardsAndAssetsSelectionBar';
import {
  getGalleryCardRadiusStyle,
  getShareLinkPageBottomCornerClass,
  getShareLinkPageCornerClass,
} from '~/components/v2/components/shared/share-link-design/shareLinkThumbnailRadius';
import { PublicWorkspaceHeader } from '~/components/v2/components/workspace/workspace-header/PublicWorkspaceHeader';
import { createBoardRoute, createShortUrlRoute } from '~/constants/routes';
import { useBodyBackground } from '~/hooks/useBodyBackground';
import { useOnMissingUserSession } from '~/hooks/useOnMissingUserSession';
import { usePublicCustomColors } from '~/hooks/usePublicCustomColors';
import { getPublicHeadlineFontFamilyValue, usePublicHeadlineFont } from '~/hooks/usePublicHeadlineFont';
import { usePublicHeadlineFontLoader } from '~/hooks/usePublicHeadlineFontLoader';
import { usePublicUrlBoardNavigation } from '~/hooks/usePublicUrlBoardNavigation';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { useAccountContext } from '~/providers/AccountProvider';
import { MouseSelectionProvider } from '~/providers/MouseSelectionProvider';
import { PublicUrlAssetModalNavigationProvider } from '~/providers/PublicUrlAssetModalNavigationProvider';
import { ScrollElementProvider } from '~/providers/ScrollElementProvider';
import { setCentralizedPublicBoardAction } from '~/store/centralizedBoard/actions';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { setConfigurableViewFieldsAction } from '~/store/configViews/actions';
import { currentViewTypeNameSelector } from '~/store/configViews/selectors';
import { useGetPublicConfigViewOptions } from '~/swr-hooks/gallery/viewFields/useGetPublicConfigViewsOptions';
import { type ClipSeoData, type PublicBoardShareData } from '~/swr-hooks/shortUrl/utils';

// React's CSSProperties has no index signature for custom properties, so the var must be named
// explicitly for the style object to typecheck without an assertion (precedent:
// PublicShowcasePage's identical var host).
// The weight/style vars are optional because they only exist when the stored font names a variant;
// without one the surfaces keep the weights they hardcoded before the vars existed (their fallbacks).
type BoardHeadlineFontVarStyle = CSSProperties & {
  '--headline-font-family': string;
  '--headline-font-weight'?: string;
  '--headline-font-style'?: string;
};

export type PublicBoardPageProps = {
  board: PublicBoardShareData['board'];
  presentationSettings?: PublicBoardShareData['presentationSettings'];
  requireAccount: PublicBoardShareData['requireAccount'];
  initialClipData?: ClipSeoData | null;
  shortId: string;
};

export const PublicBoardPage = memo(
  ({ board, presentationSettings, requireAccount, initialClipData, shortId }: PublicBoardPageProps) => {
    const router = useRouter();
    const scrollElementRef = useRef<HTMLDivElement | HTMLElement | null>(null);
    const centralizedBoard = useSelector(centralizedBoardSelector);
    const { viewerIsMemberOfPublicWorkspace } = usePublicShareWorkspaceAccess();
    const { trackClickedPublicBoardLink } = useTrackClickedPublicBoardLink();

    const { data: account } = useAccountContext();

    const [showAnonymousSignupModal] = useAirModal(PublicBoardAnonymousSignUpModal);

    const { sendPublicPageViewEvent } = usePublicPageViewNotification();
    const { pageBgColor } = usePublicCustomColors();

    // Paint the <body> with the resolved page background so overscroll behind the scroll container
    // shows the page color, not the default white body. Falls back to the same theme-aware token the
    // page wrapper's `bg-gray-4` resolves to (never hardcoded, so it stays correct in dark theme).
    useBodyBackground(pageBgColor ?? 'var(--colors-gray-4)');

    const headlineFont = usePublicHeadlineFont();
    // Injects the <head> tag that actually loads the family (Google Fonts link or @font-face);
    // no-op when the link has no headline font. Mounted here — v2 tree only — because the legacy
    // <medium tree renders its own title component that never reads the headline font, so it has
    // nothing to load.
    usePublicHeadlineFontLoader();

    useOnMissingUserSession({
      onMissingSession: () => showAnonymousSignupModal({}),
      enabled: requireAccount,
    });

    // The standalone board tree's drill action, supplied to PublicBoard's contract and the header's
    // search bar. `activePage: null` because board links have no sub-pages — drills carry no
    // `?page=` trail. Redux + react-query sit above this component (v2 PublicBoardLayout mounts
    // PublicBoardReduxProvider), which the hook's centralized-board seed requires.
    const { onNavigateToBoard } = usePublicUrlBoardNavigation({
      shortId,
      basePath: createShortUrlRoute(shortId),
      activePage: null,
    });

    const dispatch = useDispatch();
    // Always the URL: this page only ever renders on its real /a/{shortId}/b/{boardId} route (a
    // board inside a collection renders through PublicShowcaseBoardView instead).
    const boardId = useURLBoardIdSelector();
    const currentViewTypeName = useSelector(currentViewTypeNameSelector);

    const { data: configViewOptions } = useGetPublicConfigViewOptions({ viewType: currentViewTypeName });

    // Fetch board data when boardId changes
    const { data: publicBoard } = usePublicBoardPage({
      boardId: boardId || '',
      initialData: undefined,
    });

    useEffect(() => {
      if (!requireAccount || (requireAccount && !!account)) {
        sendPublicPageViewEvent(shortId);
      }
    }, [account, requireAccount, sendPublicPageViewEvent, shortId]);

    useEffect(() => {
      if (configViewOptions?.data) {
        dispatch(setConfigurableViewFieldsAction({ configurableViewFields: configViewOptions?.data }));
      }
    }, [configViewOptions?.data, dispatch]);

    // Ensure centralized board state is updated when board data changes
    useEffect(() => {
      if (publicBoard && publicBoard.id === boardId) {
        dispatch(setCentralizedPublicBoardAction({ board: publicBoard }));
      }
    }, [publicBoard, boardId, dispatch]);

    const onWorkspaceNavigate = useCallback(() => {
      if (!centralizedBoard) {
        return;
      }

      void router.push(createBoardRoute(centralizedBoard.id, centralizedBoard.title));
      trackClickedPublicBoardLink({
        name: 'clicked view in workspace',
        page_area: 'header',
        page_type: 'public board',
      });
    }, [centralizedBoard, router, trackClickedPublicBoardLink]);

    // `undefined` when the link stores no pageWidth, so the column renders unbounded exactly as
    // today (see boardPageColumn — the board's absent-value contract differs from the collection's).
    const pageMaxWidthStyle = getBoardPageMaxWidthStyle(presentationSettings?.pageWidth);
    // `undefined` for the default radius, so a legacy link's page root style stays byte-identical
    // and the shared gallery cards keep their native Tailwind radii via the vars' fallbacks.
    const galleryCardRadiusStyle = getGalleryCardRadiusStyle(presentationSettings?.thumbnailRadius);
    // `undefined` when the link has no headline font, so the shared gallery board titles (which
    // read the vars with their pre-var fallbacks) — and this root's style attr — stay byte-identical
    // to today. The weight/style vars publish the chosen variant so every headline surface APPLIES
    // the one face the loader requested; without a variant they stay unset and the surfaces keep
    // their own weights.
    const headlineFontVarStyle: BoardHeadlineFontVarStyle | undefined = headlineFont
      ? {
          '--headline-font-family': getPublicHeadlineFontFamilyValue(headlineFont.fontFamily),
          ...(headlineFont.fontWeight ? { '--headline-font-weight': String(headlineFont.fontWeight) } : undefined),
          ...(headlineFont.fontStyle ? { '--headline-font-style': headlineFont.fontStyle } : undefined),
        }
      : undefined;

    return (
      // URL-backed navigation for the standalone board page: this component only ever renders on
      // its real /a/{shortId}/b/{boardId} route (a board inside a collection renders through
      // PublicShowcaseBoardView instead, under the collection surface's provider).
      <PublicUrlAssetModalNavigationProvider>
        {/* The page root hosts the gallery-card radius vars (read by the shared GalleryAsset/
          GalleryBoard cards inside the content column), the headline-font-family var those board
          cards' titles consume when the link customizes it, and, when the link stores a pageWidth,
          the --share-link-page-max-width var the column's max-w class consumes — same mechanism as
          PublicShowcasePage, without prop threading. */}
        <div
          className="flex size-full h-screen flex-col overflow-hidden ads:bg-ads-gray-4 bg-gray-4"
          data-testid="PUBLIC_BOARD_PAGE"
          style={{
            ...pageMaxWidthStyle,
            ...galleryCardRadiusStyle,
            ...headlineFontVarStyle,
            ...(pageBgColor ? { backgroundColor: pageBgColor } : undefined),
          }}
        >
          {/* No headerActions: the v2 header is logo + search only. The board's "Save all" download menu
            now lives in PublicBoardTopbar. PublicBoardHeaderActions (Copy Link + download) is retained
            solely for the legacy <medium tree (PublicHeaderBar), pending its removal with showUI2.
            contentClassName bounds the header's inner content to the same centered column as the
            content below (the header background stays full-bleed) — gated on pageMaxWidthStyle so an
            absent pageWidth adds no wrapper and the header markup stays byte-identical to today. */}
          <PublicWorkspaceHeader
            Search={
              presentationSettings?.hideSearchBar ? undefined : (
                // biome-ignore lint/performance/noJsxPropsBind: intentional no-op close handler for embedded public search
                <PublicSearch onCloseClick={() => undefined} onNavigateToBoard={onNavigateToBoard} />
              )
            }
            contentClassName={pageMaxWidthStyle ? BOARD_PAGE_HEADER_COLUMN_CLASS_NAME : undefined}
            onWorkspaceNavigate={viewerIsMemberOfPublicWorkspace ? onWorkspaceNavigate : undefined}
          />
          {/* When a custom page color is set, frame the content card with a uniform gutter so the color
            shows on all sides; otherwise keep the original flush-top layout (px-2 pb-2, no top gap). */}
          <div className={cn('flex grow gap-2 overflow-hidden', pageBgColor ? 'p-2' : 'px-2 pb-2')}>
            <PublicBoardPageSeoTitle board={board} initialClipData={initialClipData} />
            {/* The scroller below is also the selection area (`#main`) — a plain click anywhere
              inside it clears the current selection (see useSelectedItemsMouseSelection). */}
            <MouseSelectionProvider selectionArea={scrollElementRef}>
              <ScrollElementProvider scrollElementRef={scrollElementRef}>
                <ScrollElementContextProvider>
                  <ScrollElementContextConsumer>
                    {({ setScrollElement }) => (
                      // Column corners follow the link's Corner rounding setting (rounded-12 by
                      // default). The max-width pair only applies when the link stores a pageWidth:
                      // grow fills the row up to the var's width and mx-auto centers the clamped
                      // column; absent, the column stays unbounded exactly as it ships today.
                      <div
                        className={cn(
                          'relative grow overflow-hidden ads:bg-base bg-gray-1',
                          getShareLinkPageCornerClass(presentationSettings?.thumbnailRadius),
                          pageMaxWidthStyle && 'mx-auto max-w-[var(--share-link-page-max-width)]',
                        )}
                      >
                        <main
                          data-testid="PUBLIC_BOARD_LAYOUT_MAIN"
                          className="flex h-full flex-col overflow-y-auto pb-12"
                          // biome-ignore lint/performance/noJsxPropsBind: ref must assign ref object and scroll context in one callback
                          ref={(ref) => {
                            scrollElementRef.current = ref;
                            setScrollElement(ref);
                          }}
                        >
                          {/* Standalone board share: board-only breadcrumbs, the "Save all"
                            download menu (the only PublicRootBoardProvider consumer, which this tree
                            mounts), and this surface's own items views (its public cards over an
                            unscoped fetch — a standalone board link has no collection). The
                            collection host supplies its own breadcrumbs slot, no download menu, and
                            its collection-scoped views instead — composition replacing the old
                            topbar host-check and renderer threading. */}
                          <PublicBoard
                            breadcrumbs={<PublicBoardTopbarBreadcrumbs />}
                            GalleryView={PublicBoardPageGalleryView}
                            onNavigateToBoard={onNavigateToBoard}
                            TableView={PublicBoardPageTableView}
                            topbarTrailingContent={<PublicBoardTopbarDownloadMenu />}
                          />
                        </main>
                        {centralizedBoard && (
                          // The clip's bottom corners follow the Corner rounding setting alongside the
                          // column's own (rounded-b-12 by default).
                          <div
                            className={cn(
                              'absolute inset-x-0 bottom-0 overflow-hidden',
                              getShareLinkPageBottomCornerClass(presentationSettings?.thumbnailRadius),
                            )}
                          >
                            <PublicBoardBottomBar />
                          </div>
                        )}
                      </div>
                    )}
                  </ScrollElementContextConsumer>
                </ScrollElementContextProvider>
                {centralizedBoard && <PublicBoardsAndAssetsSelectionBar />}
              </ScrollElementProvider>
            </MouseSelectionProvider>
          </div>
        </div>
      </PublicUrlAssetModalNavigationProvider>
    );
  },
);

PublicBoardPage.displayName = 'PublicBoardPage';
